/**
 * The sign-in screen.
 *
 * One button. The console does not build the Twitch URL itself — it asks the
 * API, which mints the server-side state that makes the callback safe. A
 * client that assembled that URL would be a client that could choose its own
 * `state`, and the whole flow rests on it being unguessable.
 */
import { useState, type ReactNode } from 'react';
import { loginErrorMessage } from '../lib/loginError.js';
import { auth } from '../lib/tomo.js';
import { ApiError } from '../lib/api.js';
import { Button } from '../components/primitives.jsx';
import { currentRoute } from '../lib/router.js';

/** What the callback puts in the query when a sign-in did not finish. */
export function SignIn(): ReactNode {
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const route = currentRoute();
  const reason = route.search.get('login_error');

  const start = (): void => {
    setBusy(true);
    setError(null);
    // Come back to wherever they were trying to go. A path, never a URL — the
    // server refuses anything else, and an open redirect on a login callback
    // is a credential-delivery primitive.
    const returnTo = route.path === '/' ? '/' : route.path;
    auth.start(returnTo).then(
      (url) => { window.location.href = url; },
      (err: unknown) => {
        setBusy(false);
        setError(err instanceof ApiError ? err.message : 'Could not start the sign-in.');
      }
    );
  };

  return (
    // Inside the site's frame (see SessionGate), which already says tomo.
    <div className="flex justify-center pt-8">
      <div className="w-full max-w-sm text-center">
        <h1 className="text-2xl font-semibold tracking-tight">Studio</h1>
        <p className="mt-2 text-sm text-ink-400">
          Rewards, collections and trading for your community.
        </p>

        {reason ? (
          <p role="status" className="mt-6 rounded-md border border-warn-400/40 bg-warn-400/5 p-3 text-sm text-warn-400">
            {loginErrorMessage(reason)}
          </p>
        ) : null}

        {error ? (
          <p role="alert" className="mt-6 rounded-md border border-bad-400/40 bg-bad-400/5 p-3 text-sm text-bad-400">
            {error}
          </p>
        ) : null}

        <div className="mt-8">
          <Button onClick={start} disabled={busy}>
            {busy ? 'Taking you to Twitch…' : 'Sign in with Twitch'}
          </Button>
        </div>

        <p className="mt-6 text-xs text-ink-700">
          tomo asks Twitch only who you are. It never posts as you.
        </p>
      </div>
    </div>
  );
}
